import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { Component, Injector, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { RouterModule, Router, NavigationEnd, Scroll } from '@angular/router';
import { Subject, fromEvent, filter, takeUntil, take } from 'rxjs';
import { scrollViewportToTop } from './utils/scroll-viewport-to-top.util';
import { resolveMsalAccountNames } from './utils/sso-user-names.util';
import { consumeSafeReturnUrl, peekMsalReturnUrl } from './utils/sso-return-url.util';
import { mcpConsentUrlIfMisrouted } from './utils/mcp-consent-query.util';
import { isMcpConsentPath, isMsalRedirectPath } from './utils/msal-redirect.util';
import { SeoService } from './services/seo.service';
import { NgComponentOutlet } from '@angular/common';
import type { Type } from '@angular/core';
import type { MsalService } from '@azure/msal-angular';
import type { AuthService } from './services/auth.service';
import type { ContextService } from './services/context.service';
import type { CoreUtils } from './classes/coreUtils';
import type { CookieConsentService } from './services/cookie-consent.service';
import type { UserEngagementService } from './services/user-engagement.service';
import type { GoogleAuthService } from './services/google-auth.service';
import type { PlatformVisitService } from './services/platform-visit.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterModule, NgComponentOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading = true;
  ssoRedirecturl: string | null = null;
  toolbarComponent: Type<unknown> | null = null;
  private readonly _destroying$ = new Subject<void>();
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly seoService = inject(SeoService);
  private readonly viewportScroller = inject(ViewportScroller);

  private msalService?: MsalService;
  private authService?: AuthService;
  private context?: ContextService;
  private coreUtils?: CoreUtils;
  private cookieConsent?: CookieConsentService;
  private userEngagementService?: UserEngagementService;
  private googleAuthService?: GoogleAuthService;
  private platformVisit?: PlatformVisitService;

  private loadBrowserShell(): void {
    void import('./components/top-toolbar/top-toolbar.component').then((m) => {
      this.toolbarComponent = m.TopToolbarComponent;
    });
  }

  private initBrowserServices(): void {
    void Promise.all([
      import('@azure/msal-angular'),
      import('./services/auth.service'),
      import('./services/context.service'),
      import('./classes/coreUtils'),
      import('./services/cookie-consent.service'),
      import('./services/user-engagement.service'),
      import('./services/google-auth.service'),
      import('./services/platform-visit.service'),
    ]).then(
      ([
        msal,
        auth,
        context,
        coreUtils,
        cookieConsent,
        userEngagement,
        googleAuth,
        platformVisit,
      ]) => {
        this.msalService = this.injector.get(msal.MsalService);
        this.authService = this.injector.get(auth.AuthService);
        this.context = this.injector.get(context.ContextService);
        this.coreUtils = this.injector.get(coreUtils.CoreUtils);
        this.cookieConsent = this.injector.get(cookieConsent.CookieConsentService);
        this.userEngagementService = this.injector.get(userEngagement.UserEngagementService);
        this.googleAuthService = this.injector.get(googleAuth.GoogleAuthService);
        this.platformVisit = this.injector.get(platformVisit.PlatformVisitService);
        this.platformVisit.start();
        this.startBrowserAuthFlow();
      },
    );
  }

  private openCreateAccountFlow(options?: {
    startState?: string;
    ssoData?: { email: string; forename: string; surname: string; provider: string };
  }): void {
    void import('./services/create-account-modal-flow.service').then((m) => {
      this.injector.get(m.CreateAccountModalFlowService).openCreateAccountFlow(options);
    });
  }

  ngOnInit() {
    this.seoService.listenToRouter();
    this.updateShellFullBleed(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this._destroying$),
      )
      .subscribe((e) => {
        this.updateShellFullBleed(e.urlAfterRedirects);
        this.recoverMcpConsentRoute(e.urlAfterRedirects);
      });

    if (!isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
      return;
    }

    this.router.events
      .pipe(
        filter((e): e is Scroll => e instanceof Scroll),
        takeUntil(this._destroying$),
      )
      .subscribe((e) => {
        if (e.position) {
          const position = e.position;
          requestAnimationFrame(() => this.viewportScroller.scrollToPosition(position));
          return;
        }
        if (e.anchor) {
          const anchor = e.anchor;
          requestAnimationFrame(() => this.viewportScroller.scrollToAnchor(anchor));
          return;
        }
        scrollViewportToTop(this.viewportScroller);
      });

    // Show the shell immediately; auth and toolbar load in the background.
    this.isLoading = false;
    this.loadBrowserShell();
    this.initBrowserServices();
  }

  private startBrowserAuthFlow(): void {
    if (
      !this.googleAuthService ||
      !this.msalService ||
      !this.authService ||
      !this.context ||
      !this.cookieConsent ||
      !this.userEngagementService
    ) {
      return;
    }

    this.googleAuthService.initializeGoogleSignIn();
    const pathname = this.document.defaultView?.location.pathname ?? this.router.url;
    if (isMsalRedirectPath(pathname)) {
      this.msalService.handleRedirectObservable({ navigateToLoginRequestUrl: false }).subscribe({
        next: (result) => {
          if (result && result.account) {
            this.msalService!.instance.setActiveAccount(result.account);
            this.ssoRedirecturl = result.state || '/home';
            this.checkAndLogin();
          } else if (
            this.authService!.hasVerifiedSession() &&
            this.msalService!.instance.getAllAccounts().length === 0 &&
            this.context!.getSnapshot().isMicrosoftLogin
          ) {
            this.performLocalLogoutForRedirect();
          } else {
            this.checkAndLogin();
          }
        },
        error: (err) => {
          console.error('MSAL redirect failed', err);
          this.checkAndLogin();
        },
      });
    } else if (!isMcpConsentPath(pathname)) {
      this.checkAndLogin();
    }
    this.userEngagementService.startGeneralSiteTracking();
    this.cookieConsent.openAutoIfNeeded();

    this.authService.refreshAccessTokenIfExpiredOrExpiringSoon(120);

    fromEvent(this.document, 'visibilitychange')
      .pipe(
        filter(() => this.document.visibilityState === 'visible'),
        takeUntil(this._destroying$),
      )
      .subscribe(() =>
        this.authService!.refreshAccessTokenIfExpiredOrExpiringSoon(120),
      );
  }

  checkAndLogin() {
    if (!this.context || !this.msalService || !this.authService || !this.coreUtils) {
      return;
    }

    this.context
      .getContextProperty('isSsoRegistrationFlow')
      .pipe(take(1))
      .subscribe((isSsoFlow) => {
        if (isSsoFlow) {
          this.context!.setIsSsoRegistrationFlow(false);
        }

        let activeAccount = this.msalService!.instance.getActiveAccount();
        if (!activeAccount) {
          const accounts = this.msalService!.instance.getAllAccounts();
          if (accounts.length > 0) {
            activeAccount = accounts[0];
            this.msalService!.instance.setActiveAccount(activeAccount);
          }
        }

        if (activeAccount && this.authService!.hasVerifiedSession()) {
          this.returnToStashedConsentIfWaiting();
          return;
        }

        if (activeAccount && !this.authService!.hasVerifiedSession()) {
          if (isSsoFlow) {
            this.handleSsoRegistration(activeAccount);
          } else {
            const email = activeAccount.username;
            if (!email) {
              this.coreUtils!.openSnackBar(
                'Could not retrieve email from Microsoft. Please try again.',
                'Close',
                5000,
              );
              return;
            }

            this.authService!.checkUserExistsEmail(email).subscribe((result) => {
              if (result?.exists) {
                this.msalService!
                  .acquireTokenSilent({
                    scopes: ['openid', 'profile'],
                  })
                  .subscribe((result) => {
                    const idToken = result.idToken;
                    this.authService!.loginWithMicrosoft(idToken).subscribe({
                      next: () => this.navigateAfterMicrosoftLogin(),
                      error: (err) => {
                        console.error('SSO Backend logic failed', err);
                      },
                    });
                  });
              } else {
                this.coreUtils!.openSnackBar(
                  'Account not found. Please create an account to continue.',
                  'Close',
                  5000,
                );
                this.openCreateAccountFlow();
                this.msalService!.instance.setActiveAccount(null);
              }
            });
          }
        }
      });
  }

  private handleSsoRegistration(account: {
    username?: string;
    name?: string | null;
    idTokenClaims?: Record<string, unknown> | null;
  }) {
    if (!this.authService || !this.coreUtils || !this.msalService) {
      return;
    }

    const email = account.username;
    if (!email) {
      this.coreUtils.openSnackBar(
        'Could not retrieve email from Microsoft. Please try again.',
        'Close',
        5000,
      );
      return;
    }

    this.authService.checkUserExistsEmail(email).subscribe((result) => {
      if (result?.exists) {
        this.coreUtils!.openSnackBar(
          'An account with this email already exists. Logging you in.',
          'Close',
          3000,
        );
        this.msalService!
          .acquireTokenSilent({
            scopes: ['openid', 'profile'],
          })
          .subscribe((result) => {
            this.authService!.loginWithMicrosoft(result.idToken).subscribe({
              next: () => this.navigateAfterMicrosoftLogin(),
              error: (err) =>
                console.error('SSO login failed after existence check', err),
            });
          });
      } else {
        const { forename, surname } = resolveMsalAccountNames(account);
        this.openCreateAccountFlow({
          startState: 'create-account-email-details',
          ssoData: {
            email,
            forename,
            surname,
            provider: 'microsoft',
          },
        });
      }
    });
  }

  private performLocalLogoutForRedirect() {
    if (!this.authService || !this.context) {
      return;
    }

    this.authService.logout({ email: '' }).subscribe({
      next: () => {
        this.context!.setMicrosoftLogin(false);
      },
      error: (err) => {
        console.error('Local logout failed after redirect', err);
        this.context!.setMicrosoftLogin(false);
      },
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    this.document.body.classList.remove('shell-full-bleed');
    this.userEngagementService?.stopTracking();
  }

  private navigateAfterMicrosoftLogin(): void {
    const redirectUrl = consumeSafeReturnUrl(this.ssoRedirecturl);
    this.ssoRedirecturl = null;
    void this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
  }

  /** Session already exists (refresh raced MSAL) — still return to Claude consent. */
  private returnToStashedConsentIfWaiting(): void {
    const pending = peekMsalReturnUrl();
    if (!pending?.startsWith('/mcp/consent')) {
      return;
    }
    const path = (this.router.url.split('?')[0] || '/').trim();
    if (path === '/auth' || path === '/home' || path === '/' || path === '') {
      this.navigateAfterMicrosoftLogin();
    }
  }

  private recoverMcpConsentRoute(url: string): void {
    const path = (url.split('?')[0].split('#')[0] || '/').trim();
    if (path === '/auth' || path.startsWith('/auth/')) {
      return;
    }
    // Stash-only recovery waits for a session so we do not reopen login mid-MSAL.
    const stashed = this.authService?.hasVerifiedSession() ? peekMsalReturnUrl() : null;
    const consentUrl = mcpConsentUrlIfMisrouted(url, stashed);
    if (!consentUrl || consentUrl === url) {
      return;
    }
    void this.router.navigateByUrl(consentUrl, { replaceUrl: true });
  }

  private updateShellFullBleed(url: string): void {
    let path = (url.split('?')[0].split('#')[0] || '/').trim();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    if (path === '') {
      path = '/';
    }
    const shellFullBleed =
      path === '/' ||
      path === '/home' ||
      path === '/discover' ||
      path.startsWith('/discover/') ||
      path === '/filtered-articles' ||
      path === '/marketplace' ||
      path === '/directory' ||
      path === '/experts' ||
      path.startsWith('/experts/') ||
      path === '/community' ||
      path === '/provider-hub' ||
      path === '/dictionary' ||
      path.startsWith('/dictionary/') ||
      path.startsWith('/referral/') ||
      path === '/orbit-explorer' ||
      path.startsWith('/orbit-explorer/') ||
      path === '/my-chainmart' ||
      path.startsWith('/my-chainmart/') ||
      path === '/manage-articles' ||
      path === '/admin' ||
      /^\/organisation\/[^/]+(?:\/(?!storefront)[^/]+)?$/.test(path);
    this.document.body.classList.toggle('shell-full-bleed', shellFullBleed);
  }
}
